import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';

const Header = ({ lightBackground = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, toggleLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Di halaman Product Detail (background terang), selalu gunakan gaya terlihat
  const isVisible = lightBackground || isScrolled;

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: id } });
      setIsMobileMenuOpen(false);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: t(language, 'header.home'), id: 'hero' },
    { label: t(language, 'header.about'), id: 'about' },
    { label: t(language, 'header.services'), id: 'services' },
    { label: t(language, 'header.products'), id: 'products' },
    { label: t(language, 'header.partners'), id: 'partners' },
    { label: t(language, 'header.clients'), id: 'clients' },
  ];

  // Product Detail: transparan (glass) tapi logo & teks tetap terlihat
  const headerBgClass = lightBackground 
    ? 'bg-white/60 backdrop-blur-md shadow-sm border-b border-white/20' 
    : (isVisible ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent');

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBgClass}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div 
            className={`flex items-center cursor-pointer transition-all duration-300`}
            onClick={() => scrollToSection('hero')}
          >
            <img 
              src={`${process.env.PUBLIC_URL || ''}/seleno/${isVisible ? 'logologo.png' : 'selenoreverse.png'}`} 
              alt="Seleno Logo" 
              className="h-12 sm:h-14 md:h-16 w-auto object-contain transition-opacity duration-300"
              loading="eager"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium transition-colors duration-200 hover:text-yellow-600 ${
                  isVisible ? 'text-gray-700' : 'text-white'
                }`}
              >
                {item.label}
              </button>
            ))}
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-yellow-500 hover:bg-yellow-600 text-navy font-semibold"
            >
              {t(language, 'header.contact')}
            </Button>
            
            {/* Language Toggle Button */}
            <button
              onClick={toggleLanguage}
              className="px-3 py-1.5 bg-[#996522] bg-[linear-gradient(56deg,_rgba(153,101,34,1)_0%,_rgba(255,180,41,1)_100%)] text-white font-semibold rounded-full hover:shadow-lg transition-all duration-300 text-xs uppercase tracking-wider"
            >
              {language === 'id' ? 'ID' : 'EN'} | {language === 'id' ? 'EN' : 'ID'}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              isVisible ? 'text-gray-700' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden py-4 bg-white rounded-lg shadow-xl mt-2 mb-4">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left px-6 py-3 text-gray-700 hover:bg-yellow-50 hover:text-yellow-600 transition-colors"
              >
                {item.label}
              </button>
            ))}
            <div className="px-6 pt-3 pb-3 space-y-3">
              <Button 
                onClick={() => scrollToSection('contact')}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-navy font-semibold"
              >
                {t(language, 'header.contact')}
              </Button>
              <button
                onClick={toggleLanguage}
                className="w-full px-3 py-2 bg-[#996522] bg-[linear-gradient(56deg,_rgba(153,101,34,1)_0%,_rgba(255,180,41,1)_100%)] text-white font-semibold rounded-full hover:shadow-lg transition-all duration-300 text-xs uppercase tracking-wider"
              >
                {language === 'id' ? 'ID' : 'EN'} | {language === 'id' ? 'EN' : 'ID'}
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;



